import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Rating,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  TableSortLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalanceWallet as RefundIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  InfoOutlined,
  PlayArrow
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { feedbackAPI } from '../../services/api';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';
import { getSecurePreviewUrl, getSecureVideoStreamUrl } from '../../utils/imageUrlUtils';
import { API_URLS } from '../../config/apiConfig';

// Feedback Management Component
// This component displays all feedback records in a structured table format
// with server-side pagination, sorting, and searching capabilities
// 
// Features:
// - Comprehensive feedback display with merchant details
// - Image/video preview capabilities with proper thumbnail handling
// - Rating visualization with star display
// - Token refund processing with confirmation dialog (amounts from API field credits_cost)
// - Advanced search and filtering across all fields
// - Responsive design for mobile and desktop
// - Permission-based access control
// 
// Thumbnail Implementation (EXACT SAME as GenerationHistory.js):
// - Backend endpoints: /api/merchant/ai-generation/preview/{id}?type=enhanced
// - For video generations: enhanced_image_url (primary) > generic video placeholder (fallback)
// - For image generations: generated_image_url (primary) > original_image_url (fallback) > image icon
// - Video thumbnails include play button overlay and hover effects
// - All thumbnails use SecureImage component with proper JWT authentication
// - Hover effects with scale transformations (1.05x) for better UX
// - Same URL generation logic: getSecurePreviewUrl(generationId, type)
// - Same fallback strategy and error handling
const FeedbackManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermission } = usePermissions();

  // Permission checks
  const canView = hasPermission('FeedbackManagement', 'view');
  const canProcessRefund = hasPermission('FeedbackManagement', 'update');

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackData, setFeedbackData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [searchLoading, setSearchLoading] = useState(false); // Loading state for search button

  // Sorting state
  const [sortBy, setSortBy] = useState('feedback_submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Refund dialog state
  const [refundDialog, setRefundDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  // Helper function to get secure preview URL for feedback images
  // Fixed implementation to properly handle input images vs generated images
  const getSecureFeedbackImageUrl = (imageUrl, generationId = null, isVideo = false) => {
    if (!imageUrl) return null;

    console.log('Processing image URL:', imageUrl, 'with generationId:', generationId, 'isVideo:', isVideo);

    // For input images (original_image_url), use the uploads endpoint directly
    // Input images are stored in uploads and don't need the preview endpoint
    if (imageUrl.includes('/input/') || imageUrl.includes('/uploads/')) {
      // Clean the URL and use the uploads endpoint
      let cleanPath = imageUrl;

      // Remove leading slashes and any base URL parts
      cleanPath = cleanPath.replace(/^https?:\/\/[^\/]+/, ''); // Remove domain
      cleanPath = cleanPath.replace(/^\/+/, ''); // Remove leading slashes

      // If it already starts with uploads/, use it as-is
      if (!cleanPath.startsWith('uploads/')) {
        cleanPath = `uploads/${cleanPath}`;
      }

      const secureUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/uploads/${cleanPath}`;
      console.log('Using uploads endpoint for input image:', secureUrl);
      return secureUrl;
    }

    // For generated images and videos, use the preview endpoint with generation ID
    if (generationId) {
      // For videos, use 'enhanced' type to get the enhanced image thumbnail
      const secureUrl = getSecurePreviewUrl(generationId, isVideo ? 'enhanced' : null);
      console.log('Using provided generation ID, secure URL:', secureUrl, 'type:', isVideo ? 'enhanced' : 'default');
      return secureUrl;
    }

    // Try to extract generation ID from the URL for generated images
    const match = imageUrl.match(/gen_(\d+)/);
    if (match) {
      const extractedId = match[1];
      const secureUrl = getSecurePreviewUrl(extractedId, isVideo ? 'enhanced' : null);
      console.log('Extracted gen_ ID:', extractedId, 'secure URL:', secureUrl, 'type:', isVideo ? 'enhanced' : 'default');
      return secureUrl;
    }

    // For video thumbnails, if we have a generation ID in the URL path, extract it and use secure endpoint
    if (isVideo && imageUrl.includes('/generated/')) {
      // Extract generation ID from URL like: /uploads/2025/08/25/16/generated/enhanced_686_20250825_085550.jpg
      const genMatch = imageUrl.match(/enhanced_(\d+)_/);
      if (genMatch) {
        const extractedGenId = genMatch[1];
        const secureUrl = getSecurePreviewUrl(extractedGenId, 'enhanced');
        console.log('Extracted generation ID from video URL:', extractedGenId, 'secure URL:', secureUrl);
        return secureUrl;
      }
    }

    // For product upload URLs, use the secure product image endpoint
    // These URLs are like: /uploads/2025/08/23/16/products/16_b0c0c19….jpeg
    if (imageUrl.includes('/products/')) {
      // Extract the filename from the URL
      const filenameMatch = imageUrl.match(/\/products\/([^\/]+)$/);
      if (filenameMatch) {
        const filename = filenameMatch[1];
        console.log('Extracted filename from product upload URL:', filename);

        // Use the secure product image endpoint
        const secureUrl = `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/uploads/products/${filename}`;
        console.log('Generated secure product image URL:', secureUrl);
        return secureUrl;
      }

      // If we can't extract filename, log and return null
      console.warn('Product upload URL detected, could not extract filename:', imageUrl);
      return null;
    }

    // For any other upload URLs, try to extract generation ID from filename patterns
    // Common patterns: {id}_timestamp.ext, gen_{id}_timestamp.ext, etc.
    const commonPatterns = [
      /(\d+)_\d{8}_\d{6}/,           // 686_20250825_085550
      /gen_(\d+)_\d{8}_\d{6}/,       // gen_686_20250825_085550
      /input_(\d+)_\d{8}_\d{6}/,     // input_686_20250825_085550
      /enhanced_(\d+)_\d{8}_\d{6}/,  // enhanced_686_20250825_085550
      /(\d+)_[a-f0-9]+\.(jpg|jpeg|png|webp)/, // 686_b0c0c19….jpeg
    ];

    for (const pattern of commonPatterns) {
      const match = imageUrl.match(pattern);
      if (match) {
        const extractedId = match[1];
        const secureUrl = getSecurePreviewUrl(extractedId, isVideo ? 'enhanced' : null);
        console.log(`Extracted ID using pattern ${pattern}:`, extractedId, 'secure URL:', secureUrl);
        return secureUrl;
      }
    }

    // Fallback to original URL if we can't extract ID
    console.log('Using fallback URL:', imageUrl);
    return imageUrl;
  };

  // Helper function to extract generation ID from feedback data
  // The feedback API returns AIGeneration records directly, so feedback.id IS the generation ID
  const getGenerationId = (feedback) => {
    // The id field in feedback IS the AIGeneration ID since the backend returns AIGeneration records
    return feedback.id ||
      feedback.generation_id ||
      feedback.ai_generation_id ||
      feedback.generationId ||
      feedback.aiGenerationId ||
      feedback.ai_generation?.id ||
      feedback.generation?.id;
  };
  const [processingRefund, setProcessingRefund] = useState(false);

  // Media preview state (image or video)
  const [imagePreview, setImagePreview] = useState({ open: false, url: '', title: '', isVideo: false });

  // Feedback popup state
  const [feedbackPopup, setFeedbackPopup] = useState({ open: false, feedback: null });

  // Load feedback data
  const loadFeedbackData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        search: searchTerm,
        rating: ratingFilter,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response = await feedbackAPI.getAll(params);
      console.log('Feedback data received:', response.data.feedback);

      // Debug: Log first feedback item to see structure
      if (response.data.feedback && response.data.feedback.length > 0) {
        const firstFeedback = response.data.feedback[0];
        console.log('First feedback item structure:', {
          id: firstFeedback.id,
          original_image_url: firstFeedback.original_image_url,
          generated_image_url: firstFeedback.generated_image_url,
          enhanced_image_url: firstFeedback.enhanced_image_url,
          generated_video_url: firstFeedback.generated_video_url,
          output_format: firstFeedback.output_format,
          // Log all available fields to find generation ID or other identifiers
          allFields: Object.keys(firstFeedback),
          // Check for common field names that might contain generation ID
          generation_id: firstFeedback.generation_id,
          ai_generation_id: firstFeedback.ai_generation_id,
          generationId: firstFeedback.generationId,
          aiGenerationId: firstFeedback.aiGenerationId,
          // Check for any other relevant fields
          merchant_id: firstFeedback.merchant_id,
          product_id: firstFeedback.product_id,
          upload_id: firstFeedback.upload_id,
          // Additional fields that might be relevant
          ai_generation: firstFeedback.ai_generation,
          generation: firstFeedback.generation,
          // Log the actual values to see what's available
          original_image_url_value: firstFeedback.original_image_url,
          generated_image_url_value: firstFeedback.generated_image_url,
          enhanced_image_url_value: firstFeedback.enhanced_image_url,
          generated_video_url_value: firstFeedback.generated_video_url,
        });

        // Also log a sample of the URL structure to understand the pattern
        if (firstFeedback.original_image_url) {
          console.log('Original image URL analysis:', {
            url: firstFeedback.original_image_url,
            hasInput: firstFeedback.original_image_url.includes('/input/'),
            hasGen: firstFeedback.original_image_url.includes('gen_'),
            hasTimestamp: firstFeedback.original_image_url.match(/\d{8}_\d{6}/),
            extractedId: firstFeedback.original_image_url.match(/(\d+)_\d{8}_\d{6}/)?.[1],
          });
        }
      }

      setFeedbackData(response.data.feedback || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load feedback data');
      console.error('Error loading feedback data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search feedback with current filters - called when search button is clicked
  // This function applies the current search terms and filters to get filtered results
  const searchFeedback = async () => {
    setSearchLoading(true);
    setPage(0); // Reset to first page when searching
    try {
      setError('');

      const params = {
        page: 1, // Always start from first page when searching
        per_page: rowsPerPage,
        search: searchTerm,
        rating: ratingFilter,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response = await feedbackAPI.getAll(params);
      console.log('Feedback search response:', response.data.feedback);

      setFeedbackData(response.data.feedback || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search feedback data');
      console.error('Error searching feedback data:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Load specific page when pagination changes
    loadFeedbackData();
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Load first page when rows per page changes
    loadFeedbackData();
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    // Don't reset page automatically - wait for search button click
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'rating') {
      setRatingFilter(value);
    }
    // Don't reset page automatically - wait for search button click
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  // Open refund dialog
  const openRefundDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setRefundAmount(feedback.credits_cost.toString());
    setRefundReason('');
    setRefundDialog(true);
  };

  // Close refund dialog
  const closeRefundDialog = () => {
    setRefundDialog(false);
    setSelectedFeedback(null);
    setRefundAmount('');
    setRefundReason('');
  };

  // Process refund
  const processRefund = async () => {
    if (!selectedFeedback || !refundAmount || !refundReason) {
      return;
    }

    try {
      setProcessingRefund(true);

      const refundData = {
        refund_amount: parseInt(refundAmount),
        refund_reason: refundReason,
        refunded_by: 'admin' // Will be set by backend
      };

      await feedbackAPI.processRefund(selectedFeedback.id, refundData);

      // Refresh data and close dialog
      setRefundDialog(false);
      setSelectedFeedback(null);
      setRefundAmount('');
      setRefundReason('');
      loadFeedbackData();

      // Show success message
      setError('');

      // Note: The refund will now appear in the merchant's transaction history
      // as a "REFUND" transaction type with proper description and balance tracking
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to process refund');
      console.error('Error processing refund:', err);
    } finally {
      setProcessingRefund(false);
    }
  };

  // Open media preview (image or video)
  const openImagePreview = (url, title) => {
    if (!url) {
      console.warn('Cannot open preview: No secure URL available');
      return;
    }

    console.log('Opening preview:', { url, title });

    // Ensure URL is properly formatted and doesn't trigger external link behavior
    let previewUrl = url;

    // If it's a relative URL, make it absolute to prevent external link behavior
    if (url.startsWith('/')) {
      previewUrl = `${window.location.origin}${url}`;
    } else if (!url.startsWith('http')) {
      previewUrl = `${window.location.origin}/${url}`;
    }

    // Check if this is a video file based on URL or title
    const isVideo = title.toLowerCase().includes('video') ||
      url.toLowerCase().includes('.mp4') ||
      url.toLowerCase().includes('.webm') ||
      url.toLowerCase().includes('.ogg');

    console.log('Setting preview with URL:', previewUrl, 'isVideo:', isVideo);
    console.log('Current window location:', window.location.href);
    console.log('Preview URL origin:', new URL(previewUrl).origin);

    // Prevent any external link behavior
    if (new URL(previewUrl).origin !== window.location.origin) {
      console.warn('External URL detected, this might cause new tab opening:', previewUrl);
    }

    setImagePreview({ open: true, url: previewUrl, title, isVideo });
  };

  // Close media preview
  const closeImagePreview = () => {
    setImagePreview({ open: false, url: '', title: '', isVideo: false });
  };

  // Open feedback popup
  const openFeedbackPopup = (feedback) => {
    setFeedbackPopup({ open: true, feedback });
  };

  // Close feedback popup
  const closeFeedbackPopup = () => {
    setFeedbackPopup({ open: false, feedback: null });
  };

  // Download file function - Downloads file instead of opening in new tab
  const downloadFile = async (url, filename) => {
    try {
      // Get the JWT token from cookies
      const token = document.cookie.split('access_token=')[1]?.split(';')[0];

      if (!token) {
        console.error('No authorization token found');
        setError('Authentication required for download');
        return;
      }

      // Convert relative URLs to absolute URLs if needed
      let downloadUrl = url;
      if (url.startsWith('/')) {
        downloadUrl = `${window.location.origin}${url}`;
      } else if (!url.startsWith('http')) {
        downloadUrl = `${window.location.origin}/${url}`;
      }

      console.log('Downloading from URL:', downloadUrl);

      // First, get the file to determine its type and size
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You may not have permission to download this file.');
        } else if (response.status === 404) {
          throw new Error('File not found. The file may have been moved or deleted.');
        } else {
          throw new Error(`Download failed with status: ${response.status}`);
        }
      }

      // Get the content type and determine file extension
      const contentType = response.headers.get('content-type') || '';
      const contentDisposition = response.headers.get('content-disposition') || '';

      // Extract filename from content-disposition header if available
      let finalFilename = filename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          finalFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // If no filename, generate one based on content type
      if (!finalFilename || finalFilename === 'download') {
        const extension = contentType.includes('image/') ?
          contentType.split('/')[1] :
          contentType.includes('video/') ?
            contentType.split('/')[1] :
            'bin';
        finalFilename = `download.${extension}`;
      }

      // Get the blob data
      const blob = await response.blob();

      // Create a more aggressive download link
      const link = document.createElement('a');
      link.style.display = 'none'; // Hide the link
      link.download = finalFilename; // Force download
      link.target = '_self'; // Ensure same window
      link.rel = 'noopener noreferrer'; // Prevent external behavior

      // Create object URL from blob
      const blobUrl = window.URL.createObjectURL(blob);
      link.href = blobUrl;

      // Add to DOM, click, and remove immediately
      document.body.appendChild(link);

      // Force download behavior
      link.setAttribute('download', finalFilename);
      link.setAttribute('target', '_self');

      // Trigger download
      link.click();

      // Clean up immediately
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      console.log('Download completed successfully:', finalFilename);

      // Clear any errors
      setError('');

    } catch (error) {
      console.error('Download failed:', error);
      setError(`Download failed: ${error.message}`);
    }
  };

  // Format date in DD/MM/YYYY format for better readability
  // Uses toLocaleDateString with timezone options to ensure consistent Indian time display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // Use toLocaleDateString with timezone options for consistent Indian time display
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  // Format date and time: DD/MM/YYYY with 12-hour time in IST.
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    // en-GB keeps day/month/year order; en-US would show MM/DD/YYYY
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get rating color
  const getRatingColor = (rating) => {
    if (rating >= 4) return 'success';
    if (rating >= 3) return 'warning';
    return 'error';
  };

  // Load data on component mount and when dependencies change
  // Note: Search terms and filters are applied only when search button is clicked
  useEffect(() => {
    if (canView) {
      loadFeedbackData();
    }
  }, [page, rowsPerPage, sortBy, sortOrder, canView]);

  // Debug: Monitor for new tab opening attempts
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      console.log('Before unload event detected:', e);
    };

    const handlePopState = (e) => {
      console.log('Pop state event detected:', e);
    };

    // Monitor for any navigation attempts
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Prevent new tab opening by intercepting click events on links
    const preventNewTabs = (e) => {
      const target = e.target.closest('a');
      if (target && (target.target === '_blank' || target.target === '_new')) {
        console.log('Preventing new tab opening:', target.href);
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Prevent new window opening
    const preventNewWindows = (e) => {
      if (e.target && e.target.tagName === 'A') {
        const link = e.target;
        if (link.target === '_blank' || link.target === '_new') {
          console.log('Preventing new window opening:', link.href);
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
    };

    // Add event listeners
    document.addEventListener('click', preventNewTabs, true);
    document.addEventListener('click', preventNewWindows, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', preventNewTabs, true);
      document.removeEventListener('click', preventNewWindows, true);
    };
  }, []);

  // Permission check
  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to view the Feedback Management module.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Feedback Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and manage user feedback for AI generations with token refund capabilities
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Feedback
              </Typography>
              <Typography variant="h4" component="div">
                {totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Average Rating
              </Typography>
              <Typography variant="h4" component="div">
                {feedbackData.length > 0
                  ? (feedbackData.reduce((sum, item) => sum + (item.feedback_rating || 0), 0) /
                    feedbackData.filter(item => item.feedback_rating).length).toFixed(1)
                  : '0.0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Low Ratings (1-2)
              </Typography>
              <Typography variant="h4" component="div">
                {feedbackData.filter(item => item.feedback_rating && item.feedback_rating <= 2).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                High Ratings (4-5)
              </Typography>
              <Typography variant="h4" component="div">
                {feedbackData.filter(item => item.feedback_rating && item.feedback_rating >= 4).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      {/* Note: Filters are applied only when the Search button is clicked to prevent automatic loading */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search feedback, merchant, or comments..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Rating Filter</InputLabel>
              <Select
                value={ratingFilter}
                label="Rating Filter"
                onChange={(e) => handleFilterChange('rating', e.target.value)}
              >
                <MenuItem value="">All Ratings</MenuItem>
                <MenuItem value="1">1 Star</MenuItem>
                <MenuItem value="2">2 Stars</MenuItem>
                <MenuItem value="3">3 Stars</MenuItem>
                <MenuItem value="4">4 Stars</MenuItem>
                <MenuItem value="5">5 Stars</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={searchFeedback}
              disabled={searchLoading}
              sx={{ height: '56px', width: '100%' }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadFeedbackData}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}



      {/* Feedback Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'merchant_name'}
                    direction={sortBy === 'merchant_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('merchant_name')}
                  >
                    Merchant Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Input Image</TableCell>
                <TableCell>Output Generated</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'created_at'}
                    direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Generation Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Feedback Given</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'feedback_rating'}
                    direction={sortBy === 'feedback_rating' ? sortOrder : 'asc'}
                    onClick={() => handleSort('feedback_rating')}
                  >
                    Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'feedback_submitted_at'}
                    direction={sortBy === 'feedback_submitted_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('feedback_submitted_at')}
                  >
                    Feedback Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : feedbackData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No feedback records found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                feedbackData.map((feedback) => (
                  <TableRow key={feedback.id} hover>
                    {/* Merchant Name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {feedback.merchant?.name?.charAt(0)?.toUpperCase() || 'M'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {feedback.merchant?.name || 'Unknown Merchant'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {feedback.merchant?.email || 'No email'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Input Image */}
                    <TableCell>
                      {feedback.original_image_url ? (
                        (() => {
                          // Try to find the correct generation ID from various possible fields
                          const generationId = getGenerationId(feedback);

                          console.log('Input image - looking for generation ID:', {
                            feedbackId: feedback.id,
                            possibleGenerationId: generationId,
                            originalImageUrl: feedback.original_image_url,
                            hasGenerationId: !!generationId
                          });

                          const secureUrl = getSecureFeedbackImageUrl(feedback.original_image_url, generationId);
                          return secureUrl ? (
                            <Tooltip title="Click to preview input image">
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  bgcolor: 'grey.100',
                                  borderRadius: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: 1,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openImagePreview(secureUrl, `Input Image - Generation #${feedback.id}`);
                                  }}
                                >
                                  <SecureImage
                                    src={secureUrl}
                                    alt="Input Image"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 1
                                    }}
                                    onError={(e) => {
                                      console.log('Input image failed to load for feedback:', feedback.id, 'URL:', secureUrl);
                                    }}
                                    onLoad={(e) => {
                                      console.log('Input image loaded successfully for feedback:', feedback.id, 'URL:', secureUrl);
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Image not available (needs secure endpoint)">
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  bgcolor: 'grey.100',
                                  borderRadius: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'grey.500',
                                  border: '2px dashed',
                                  borderColor: 'grey.300'
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 24 }} />
                              </Box>
                            </Tooltip>
                          );
                        })()
                      ) : (
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            bgcolor: 'grey.100',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'grey.400',
                            border: '2px dashed',
                            borderColor: 'grey.300'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 24 }} />
                        </Box>
                      )}
                    </TableCell>

                    {/* Output Generated */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Generated Image Thumbnail - Using same logic as GenerationHistory.js */}
                        {feedback.generated_image_url && (
                          (() => {
                            // The feedback.id IS the generation ID since the backend returns AIGeneration records directly
                            const generationId = getGenerationId(feedback);
                            // Use getSecurePreviewUrl directly like GenerationHistory.js does
                            const secureUrl = getSecurePreviewUrl(generationId);
                            return (
                              <Tooltip title="Click to preview generated image">
                                <Box
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    borderRadius: 2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openImagePreview(secureUrl, `Generated Image - Generation #${feedback.id}`);
                                  }}
                                >
                                  <SecureImage
                                    src={secureUrl}
                                    alt="Generated Image"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 1
                                    }}
                                    onError={(e) => {
                                      console.log('Generated image failed to load for feedback:', feedback.id, 'URL:', secureUrl);
                                    }}
                                    onLoad={(e) => {
                                      console.log('Generated image loaded successfully for feedback:', feedback.id, 'URL:', secureUrl);
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            );
                          })()
                        )}

                        {/* Generated Video Thumbnail - Using exact same logic as GenerationHistory.js */}
                        {feedback.generated_video_url && (
                          <Tooltip title="Click to preview generated video">
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                bgcolor: 'grey.100',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {/* Video thumbnail - use enhanced image if available, otherwise show generic video placeholder */}
                              {feedback.enhanced_image_url ? (
                                // Video thumbnail with enhanced image and play button overlay
                                <Box
                                  sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: 1,
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Use getSecureVideoStreamUrl for video preview - same as GenerationHistory.js
                                    const generationId = getGenerationId(feedback);
                                    const videoStreamUrl = getSecureVideoStreamUrl(generationId);
                                    openImagePreview(videoStreamUrl, `Generated Video - Generation #${feedback.id}`);
                                  }}
                                >
                                  <SecureImage
                                    src={getSecurePreviewUrl(getGenerationId(feedback), "enhanced")}
                                    alt="video preview"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 1
                                    }}
                                    onError={(e) => {
                                      // Enhanced image thumbnail failed to load for video generation
                                      console.log('Enhanced image failed to load, showing fallback for feedback:', feedback.id);
                                    }}
                                    onLoad={(e) => {
                                      // Enhanced image thumbnail loaded successfully for video generation
                                      console.log('Enhanced image loaded successfully for feedback:', feedback.id);
                                    }}
                                  />
                                  {/* Play button overlay - using PlayArrow like GenerationHistory.js */}
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                                      borderRadius: '50%',
                                      width: '20px',
                                      height: '20px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <PlayArrow sx={{ color: 'white', fontSize: 12 }} />
                                  </Box>
                                </Box>
                              ) : (
                                // Generic video placeholder when no enhanced image is available
                                <Box
                                  sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: 1,
                                    bgcolor: '#1e293b',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      bgcolor: '#374151',
                                      transform: 'scale(1.05)',
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Use getSecureVideoStreamUrl for video preview - same as GenerationHistory.js
                                    const generationId = getGenerationId(feedback);
                                    const videoStreamUrl = getSecureVideoStreamUrl(generationId);
                                    openImagePreview(videoStreamUrl, `Generated Video - Generation #${feedback.id}`);
                                  }}
                                >
                                  <PlayArrow
                                    sx={{
                                      color: 'white',
                                      fontSize: 24,
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)'
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'white',
                                      fontSize: 8,
                                      position: 'absolute',
                                      bottom: 2,
                                      right: 2,
                                      fontWeight: 600
                                    }}
                                  >
                                    VIDEO
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        )}

                        {/* Output Format Chip */}
                        <Chip
                          label={feedback.output_format?.display_name || 'Unknown'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            fontSize: '0.75rem',
                            height: 24,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    </TableCell>

                    {/* Generation Date */}
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(feedback.created_at)}
                      </Typography>
                    </TableCell>

                    {/* Feedback Given */}
                    <TableCell>
                      <Tooltip title={feedback.feedback_comment ? "Click to view full feedback" : "No comment provided"}>
                        <Box
                          sx={{
                            maxWidth: 200,
                            cursor: feedback.feedback_comment ? 'pointer' : 'default',
                            '&:hover': feedback.feedback_comment ? { backgroundColor: 'action.hover', borderRadius: 1 } : {}
                          }}
                          onClick={() => feedback.feedback_comment && openFeedbackPopup(feedback)}
                        >
                          <Typography variant="body2" noWrap>
                            {feedback.feedback_comment || 'No comment provided'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Rating */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating
                          value={feedback.feedback_rating || 0}
                          readOnly
                          size="small"
                          emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                        />
                        <Chip
                          label={`${feedback.feedback_rating || 0}/5`}
                          size="small"
                          color={getRatingColor(feedback.feedback_rating)}
                          variant="outlined"
                        />
                      </Box>
                    </TableCell>

                    {/* Feedback Date */}
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(feedback.feedback_submitted_at)}
                      </Typography>
                    </TableCell>

                    {/* Action */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {canProcessRefund && !feedback.is_refunded && (
                          <Tooltip title="Process token refund">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => openRefundDialog(feedback)}
                              disabled={feedback.is_refunded}
                            >
                              <RefundIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {feedback.is_refunded && (
                          <Chip
                            label="Refunded"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialog}
        onClose={() => setRefundDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <RefundIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Process token refund
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Issue a refund for this feedback record ({selectedFeedback?.credits_cost || 0} tokens)
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedFeedback && (
            <Box sx={{ p: 4 }}>
              {/* Generation Info Card */}
              <Card
                sx={{
                  mb: 4,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  boxShadow: 'none'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      #{selectedFeedback.id}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Generation #{selectedFeedback.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedFeedback.category?.display_name || 'N/A'} • {selectedFeedback.output_format?.display_name || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${selectedFeedback.credits_cost} tokens`}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Merchant</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {selectedFeedback.merchant?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedFeedback.merchant?.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Feedback Rating</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating
                          value={selectedFeedback.feedback_rating || 0}
                          readOnly
                          size="small"
                          emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                        />
                        <Chip
                          label={`${selectedFeedback.feedback_rating || 0}/5`}
                          size="small"
                          color={getRatingColor(selectedFeedback.feedback_rating)}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Refund Form */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Refund Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Refund amount (tokens)"
                      type="number"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#f8fafc',
                          '& fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#f8fafc',
                            '& fieldset': {
                              borderColor: '#d1d5db',
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                          color: '#374151',
                          '&.Mui-disabled': {
                            color: '#374151',
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: '#1f2937',
                          fontWeight: 600,
                          fontSize: '1.1rem',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, color: '#6b7280' }}>
                            <RefundIcon sx={{ fontSize: 20 }} />
                          </Box>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Full amount will be refunded: {selectedFeedback.credits_cost} tokens
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          backgroundColor: '#fef3c7',
                          borderRadius: 2,
                          border: '1px solid #f59e0b',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" color="#92400e" fontWeight="600">
                          Original Cost
                        </Typography>
                        <Typography variant="h5" color="#92400e" fontWeight="bold">
                          {selectedFeedback.credits_cost} tokens
                        </Typography>
                      </Box>
                      <Box sx={{ color: '#6b7280' }}>
                        <RefundIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          p: 2,
                          backgroundColor: '#fee2e2',
                          borderRadius: 2,
                          border: '1px solid #ef4444',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="body2" color="#b91c1c" fontWeight="600">
                          Refund Amount
                        </Typography>
                        <Typography variant="h5" color="#b91c1c" fontWeight="bold">
                          {selectedFeedback.credits_cost} tokens
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Refund Reason"
                      multiline
                      rows={4}
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Please provide a detailed reason for this refund..."
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: '#d1d5db',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9ca3af',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ef4444',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          fontWeight: 500,
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <Box sx={{ mr: 1, color: '#6b7280', mt: 1 }}>
                            <InfoOutlined sx={{ fontSize: 20 }} />
                          </Box>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      This reason will be visible to the merchant and recorded for audit purposes
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setRefundDialog(false)}
            variant="outlined"
            disabled={processingRefund}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={processRefund}
            variant="contained"
            disabled={processingRefund || !refundAmount || !refundReason}
            startIcon={processingRefund ? <CircularProgress size={20} color="inherit" /> : <RefundIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.4)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                boxShadow: 'none'
              }
            }}
          >
            {processingRefund ? 'Processing Refund...' : 'Process Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Popup Dialog */}
      <Dialog
        open={feedbackPopup.open}
        onClose={closeFeedbackPopup}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 3,
            pt: 3,
            px: 4,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              <InfoOutlined sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h4" component="span" fontWeight="bold">
                Feedback Details
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                View complete feedback for generation #{feedbackPopup.feedback?.id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {feedbackPopup.feedback && (
            <Box sx={{ p: 4 }}>
              {/* Generation Info Card */}
              <Card
                sx={{
                  mb: 4,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  boxShadow: 'none'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        backgroundColor: '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}
                    >
                      #{feedbackPopup.feedback.id}
                    </Box>
                    <Box flex={1}>
                      <Typography variant="h6" fontWeight="bold" color="text.primary">
                        Generation #{feedbackPopup.feedback.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feedbackPopup.feedback.category?.display_name || 'N/A'} • {feedbackPopup.feedback.output_format?.display_name || 'N/A'}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${feedbackPopup.feedback.credits_cost} tokens`}
                      variant="outlined"
                      sx={{
                        fontWeight: 600,
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Merchant</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {feedbackPopup.feedback.merchant?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feedbackPopup.feedback.merchant?.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Feedback Rating</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating
                          value={feedbackPopup.feedback.feedback_rating || 0}
                          readOnly
                          size="small"
                          emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                        />
                        <Chip
                          label={`${feedbackPopup.feedback.feedback_rating || 0}/5`}
                          size="small"
                          color={getRatingColor(feedbackPopup.feedback.feedback_rating)}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Feedback Content */}
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                  Feedback Comment
                </Typography>

                <Card
                  sx={{
                    backgroundColor: '#fefefe',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    boxShadow: 'none'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {feedbackPopup.feedback.feedback_comment || 'No feedback comment provided'}
                    </Typography>
                  </CardContent>
                </Card>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Feedback submitted on {formatDateTime(feedbackPopup.feedback.feedback_submitted_at)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={closeFeedbackPopup}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Popup Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={closeImagePreview}
        maxWidth="lg"
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            pt: 1.5,
            px: 2.5,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              {imagePreview.isVideo ?
                <VideoFileIcon sx={{ fontSize: 18 }} /> :
                <ImageIcon sx={{ fontSize: 18 }} />
              }
            </Box>
            <Box>
              <Typography variant="h6" component="span" fontWeight="bold">
                {imagePreview.title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                {imagePreview.isVideo ? 'Video Preview' : 'Image Preview'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, textAlign: 'center' }}>
          {imagePreview.url && (
            imagePreview.isVideo ? (
              <SecureVideo
                src={imagePreview.url}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain'
                }}
                controls
              />
            ) : (
              <SecureImage
                src={imagePreview.url}
                alt={imagePreview.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain'
                }}
              />
            )
          )}

          {/* Show error message if download failed */}
          {error && (
            <Alert severity="error" sx={{ mt: 2, maxWidth: '100%' }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={closeImagePreview}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: 1.5,
              px: 2,
              py: 0.75,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Close
          </Button>
          {imagePreview.url && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={async (e) => {
                // Prevent any default behavior that might open new tabs
                e.preventDefault();
                e.stopPropagation();

                try {
                  // Extract filename from URL or generate one
                  const urlParts = imagePreview.url.split('/');
                  const filename = urlParts[urlParts.length - 1] || 'download';

                  // Clear any previous errors
                  setError('');

                  // Attempt download
                  await downloadFile(imagePreview.url, filename);
                } catch (error) {
                  console.error('Download button error:', error);
                  setError(`Download failed: ${error.message}`);
                }
              }}
              variant="contained"
              size="small"
              sx={{
                borderRadius: 1.5,
                px: 2,
                py: 0.75,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)',
                }
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbackManagement; 